import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import * as CommunityAPI from '@/apis/community';
import { useIdentityStore } from '@/stores/identity';
import { useNotificationStore } from '@/stores/notification';
import {
  COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP,
  COMMUNITY_INTERACTION_RULES_SAVE_AND_APPLY_TO_EXISTING_POPUP,
} from '@/app/community/constants';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import {
  createOrEditMessageRuleEvent,
  messageRuleInteractionsEvent,
  undoMessageRuleEvent,
  messageRulesSelectedEvent,
} from '@/app/community/utils/mixpanel-events';
import { useAuthStore } from '@/stores/auth';
import { useCommunityMessageRuleBuilder } from '@/composables/community/useCommunityRuleBuilder';
import {
  AI_CATEGORY_FILTER_OPTION,
  COMMUNITY_INTERACTION_RULE_TOASTS,
  MESSAGE_RULE_MIXPANEL_CONSTANTS,
  RULE_ACTION_KEYS,
  messageRuleToggleToast,
} from '@/app/community/constants/inboxAutomation';
import { refreshCancelToken } from '@/apis/axios.utils';
import { logger } from '@/utils/logger';
import * as AuthAPI from '@/apis/auth';
import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { remove } from 'lodash-es';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';

export const useCommunityInteractionRulesStore = defineStore('communityInteractionRules', () => {
  const identityStore = useIdentityStore();
  const notificationStore = useNotificationStore();
  const { showConfirmationDialog: undoRuleActionDialog } = useConfirmationDialog({
    group: 'global',
    header: COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP.title,
    message: COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP.confirmMessage,
    acceptLabel: COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP.confirmAlias,
    acceptSeverity: 'primary',
  });
  const { addToast } = useToast({ life: 3000 });
  const { launchDrawer } = useDrawer();
  const { canAccessAutomationRuleGDI } = useCommunityPermissions();

  const interactionRules = ref([]);
  const selectedBrandIdForInteractionRules = ref();
  const selectedBrandAccessibleUsers = ref([]);

  const showPopup = ref({
    messageRules: false,
    messageRuleBuilder: false,
  });
  const editedRuleId = ref();
  const pending = ref({
    interactionRules: true,
    interactionRule: false,
    aiCategories: true,
  });
  const switchedToNestedDrawer = ref(false);

  const selectedBrandAiCategories = ref([]);
  const pendingAiCategory = ref();

  const hasInteractionRules = computed(() => interactionRules.value.length > 0);

  const editedRule = computed(() => {
    if (editedRuleId.value) {
      return interactionRules.value.find((r) => r.id === editedRuleId.value);
    }
    return undefined;
  });

  const selectedBrandId = computed(
    () => selectedBrandIdForInteractionRules.value ?? identityStore.currentBrand?.id,
  );

  const selectedBrand = computed(
    () => identityStore.identityBrandsById[selectedBrandId.value] ?? identityStore.currentBrand,
  );

  const selectedBrandName = computed(
    () =>
      identityStore.identityBrandsById[selectedBrandId.value]?.name ??
      identityStore.currentBrand?.name,
  );

  const interactionRulesByBrand = computed(() => {
    if (!interactionRules.value?.length) {
      return {};
    }
    return groupBy(interactionRules.value, 'brandId');
  });

  function getSelectedBrandInteractionRules(brandId = selectedBrandId.value) {
    const brandInteractionRules = interactionRulesByBrand.value?.[brandId] ?? [];
    return orderBy(brandInteractionRules, [(r) => r.lastModifiedAt ?? r.createdAt], ['desc']);
  }

  const {
    actions,
    conditionGroups,
    isFiltersComplete,
    isActionsComplete,
    isRuleComplete,
    hasRepliedFilter,
    filterMatchedMessagesCount,
    ruleBuilderErrors,
    ruleDetail,
    ruleChannelSet,
    isSingleChannelRule,
    hasMultiChannelSelection,
    isChannelFilterOrCondition,
    buildJSONRule,
    updateGroupOperator,
    addGroupAfter,
    updateGroup,
    removeFilterGroup,
    buildJSONRuleFilters,
    buildJSONRuleActions,
    checkGroupHasCompleteItem,
    clearRuleBuilder,
    updateFilterMatchCount,
    setRuleActionsFromJSON,
    setRuleFiltersFromJSON,
    isRuleDetailChanged,
    getFilterUsed,
    getFilterGroups,
    clearFilterMessageCountAndError,
  } = useCommunityMessageRuleBuilder();

  async function getInteractionRulesForBrands(brandIds) {
    pending.value.interactionRules = true;
    const interactionRuleBrandIds = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
    try {
      const res = await CommunityAPI.getInteractionRulesForBrands(interactionRuleBrandIds);
      interactionRules.value = res.data.data;
    } catch {
      pending.value.interactionRules = false;
    } finally {
      pending.value.interactionRules = false;
    }
  }

  async function getAiCategoriesForSelectedBrand() {
    pending.value.aiCategories = true;
    try {
      const res = await CommunityAPI.getAiCategoriesForBrands([selectedBrandId.value]);
      selectedBrandAiCategories.value = res.data.data;
    } catch {
      pending.value.aiCategories = false;
    } finally {
      pending.value.aiCategories = false;
    }
  }

  async function confirmSaveAndApplyToExisting(applyToHistorical) {
    if (applyToHistorical) {
      const { title, confirmMessage, confirmAlias, cancelAlias } =
        COMMUNITY_INTERACTION_RULES_SAVE_AND_APPLY_TO_EXISTING_POPUP;
      const confirmMessageWithCount = confirmMessage.replace(
        'NUM',
        filterMatchedMessagesCount.value,
      );
      const confirmed = await notificationStore.confirm(title, confirmMessageWithCount, {
        confirmType: 'primary',
        confirmAlias,
        cancelAlias,
      });
      return confirmed;
    }
    return true;
  }

  function extractRuleFilters(ruleFilters, filterKey) {
    const items = [];
    if (!ruleFilters?.items?.length) return items;

    ruleFilters.items.forEach((item) => {
      // Match if key matches
      if (item.key === filterKey) {
        items.push(item);
      }
      // Only recurse if item has nested items
      if (item.items) {
        items.push(...extractRuleFilters(item, filterKey));
      }
    });
    return items;
  }

  function updateRuleFilterByKey(ruleFilters, filterKey, updates = {}) {
    if (!ruleFilters?.items?.length) return ruleFilters;

    return {
      ...ruleFilters,
      items: ruleFilters.items.map((item) => {
        const updatedItem = item.key === filterKey ? { ...item, ...updates } : { ...item };

        if (item.items) {
          updatedItem.items = updateRuleFilterByKey(item, filterKey, updates).items;
        }

        return updatedItem;
      }),
    };
  }

  async function createAiCategory({ name, prompt, brandId = selectedBrandId.value }) {
    try {
      const res = await CommunityAPI.createAiCategory({ brandId, name, prompt });
      return res.data;
    } catch (e) {
      logger.error('Error occurred while creating an AI Category', {}, e);
      throw e;
    }
  }

  async function updateRuleFiltersAiCategory(
    payload,
    currentAiCategory,
    pendingValue = 'interactionRule',
  ) {
    // if an unsaved AI category exists, create it first;
    // otherwise, update the existing AI category in the payload to send only its ID
    if (currentAiCategory?.value?.id !== undefined) {
      payload.detail.filters = updateRuleFilterByKey(
        payload.detail.filters,
        AI_CATEGORY_FILTER_OPTION.key,
        {
          value: currentAiCategory.value.id,
        },
      );
      return;
    }
    if (currentAiCategory?.value?.name && currentAiCategory?.value?.prompt) {
      try {
        const aiCategoryRes = await createAiCategory({
          name: currentAiCategory.value.name,
          prompt: currentAiCategory.value.prompt,
          brandId: selectedBrandId.value,
        });
        if (aiCategoryRes?.data) {
          pendingAiCategory.value = aiCategoryRes.data;
          // update the AI category filter in the rule detail filters
          payload.detail.filters = updateRuleFilterByKey(
            payload.detail.filters,
            AI_CATEGORY_FILTER_OPTION.key,
            {
              value: aiCategoryRes.data.id,
            },
          );
        }
      } catch (error) {
        logger.error(
          `Error occurred while creating a new AI Category (brand: ${selectedBrandId.value})`,
          {},
          error,
        );
        pendingAiCategory.value = undefined;
        if (pendingValue) {
          pending.value[pendingValue] = false;
        }
        addToast({
          message: 'Something went wrong. Please try again.',
          severity: 'error',
        });
        throw error;
      }
    }
  }

  async function deleteAiCategory(aiCategoryId) {
    if (aiCategoryId) {
      try {
        await CommunityAPI.deleteAiCategory(aiCategoryId);
        // remove deleted AI category from the brand AI category list
        remove(selectedBrandAiCategories.value, (c) => c.id === aiCategoryId);
      } catch (categoryErr) {
        logger.error(
          `Error occurred while deleted an AI Category (id: ${aiCategoryId}, brand: ${selectedBrandId.value})`,
          {},
          categoryErr,
        );
      }
    }
  }

  async function createInteractionRule({ name, detail, applyToHistorical = false, successToast }) {
    pending.value.interactionRule = true;
    pendingAiCategory.value = undefined;
    const payload = {
      name,
      detail,
      authorId: identityStore.identity?.id,
      enabled: true,
      applyToHistorical,
    };
    const [currentAiCategory] = extractRuleFilters(
      { ...payload.detail.filters },
      AI_CATEGORY_FILTER_OPTION.key,
    );
    await updateRuleFiltersAiCategory(payload, currentAiCategory);

    try {
      const res = await CommunityAPI.createInteractionRule(selectedBrandId.value, payload);
      interactionRules.value.unshift({
        ...res.data,
        new: true,
      });
      if (pendingAiCategory.value) {
        // add new AI category to the brand AI category list
        selectedBrandAiCategories.value.push(pendingAiCategory.value);
        pendingAiCategory.value = undefined;
      }
      const toast = successToast.message ? successToast : { message: successToast };
      addToast({ ...toast, severity: 'success' });
      return res.data;
    } catch (error) {
      const message =
        [409, 400].includes(error.response.status) && error.response.data?.error
          ? error.response.data.error
          : 'Something went wrong. Please try again.';
      addToast({ message, severity: 'error' });

      // delete AI category created on rule creation error
      if (pendingAiCategory.value) {
        await deleteAiCategory(pendingAiCategory.value.id);
        pendingAiCategory.value = undefined;
      }
      throw error;
    } finally {
      pending.value.interactionRule = false;
    }
  }

  async function createInteractionRuleLegacy(name, detail, applyToHistorical = false) {
    const notification = { message: COMMUNITY_INTERACTION_RULE_TOASTS.SUCCESS };
    const payload = { name, detail };
    payload.authorId = identityStore.identity?.id;
    payload.enabled = true;
    payload.applyToHistorical = applyToHistorical;

    if (payload.applyToHistorical) {
      notification.message = `${notification.message}. ${COMMUNITY_INTERACTION_RULE_TOASTS.APPLY_TO_EXISTING}`;
    }

    const confirmed = await confirmSaveAndApplyToExisting(applyToHistorical);
    if (!confirmed) {
      return false;
    }
    try {
      const res = await CommunityAPI.createInteractionRule(selectedBrandId.value, payload);

      interactionRules.value.unshift({
        ...res.data,
        new: true,
      });

      notificationStore.setToast(notification);
    } catch (error) {
      const message =
        [409, 400].includes(error.response.status) && error.response.data?.error
          ? error.response.data.error
          : COMMUNITY_INTERACTION_RULE_TOASTS.FAILURE;
      notificationStore.setToast({
        message,
        type: 'error',
      });
      throw error;
    }
    return true;
  }

  async function updateInteractionRule({
    ruleId,
    payload,
    successToast,
    ruleModified = false,
    cancelToken = null,
  }) {
    pending.value.interactionRule = true;
    let updatedRule;
    const [existingAiCategoryFilter] = payload.detail
      ? extractRuleFilters({ ...editedRule.value.detail.filters }, AI_CATEGORY_FILTER_OPTION.key)
      : [];
    const [currentAiCategory] = payload.detail
      ? extractRuleFilters({ ...payload.detail.filters }, AI_CATEGORY_FILTER_OPTION.key)
      : [];
    await updateRuleFiltersAiCategory(payload, currentAiCategory);

    try {
      let axiosCancelToken = null;
      if (cancelToken) {
        const { key, value } = cancelToken;
        axiosCancelToken = refreshCancelToken(value, key);
      }
      const res = await CommunityAPI.updateInteractionRule(selectedBrandId.value, ruleId, payload, {
        cancelToken: axiosCancelToken,
      });
      updatedRule = res.data;
      const existingRuleIndex = interactionRules.value.findIndex((rule) => rule.id === ruleId);
      if (ruleModified) {
        interactionRules.value.splice(existingRuleIndex, 1);
        interactionRules.value.unshift(updatedRule);
      } else {
        interactionRules.value.splice(existingRuleIndex, 1, updatedRule);
      }
      if (pendingAiCategory.value) {
        // add new AI category to the brand AI category list
        selectedBrandAiCategories.value.push(pendingAiCategory.value);
      }
      const toast = successToast.message ? successToast : { message: successToast };
      addToast({ ...toast, severity: 'success' });
    } catch (ruleErr) {
      const message =
        ruleErr.response?.status === 409 && ruleErr.response.data?.error
          ? ruleErr.response.data.error
          : 'Something went wrong. Please try again.';
      addToast({ message, severity: 'error' });

      // delete AI category created on rule update error
      if (pendingAiCategory.value) {
        await deleteAiCategory(pendingAiCategory.value.id);
        pendingAiCategory.value = undefined;
      }
      throw ruleErr;
    }

    // delete existing AI category if the current AI category has different ID
    if (
      !!updatedRule &&
      !!existingAiCategoryFilter?.value &&
      existingAiCategoryFilter.value !== currentAiCategory?.value?.id
    ) {
      await deleteAiCategory(existingAiCategoryFilter.value);
    }
    pendingAiCategory.value = undefined;
    pending.value.interactionRule = false;
    return updatedRule;
  }

  async function updateInteractionRuleLegacy(
    ruleId,
    payload,
    reactive = false,
    ruleModified = false,
    cancelToken = null,
  ) {
    const notification = { message: COMMUNITY_INTERACTION_RULE_TOASTS.SUCCESS };
    if (reactive) {
      notification.message = COMMUNITY_INTERACTION_RULE_TOASTS.SUCCESS_REACTIVATED;
    }
    if (!ruleModified && payload.enabled !== undefined) {
      notification.message = messageRuleToggleToast(payload.enabled);
    }
    if (payload.applyToHistorical) {
      notification.message = `${notification.message}. ${COMMUNITY_INTERACTION_RULE_TOASTS.APPLY_TO_EXISTING}`;
    }
    const confirmed = await confirmSaveAndApplyToExisting(payload.applyToHistorical);
    if (!confirmed) {
      return false;
    }
    try {
      let axiosCancelToken = null;
      if (cancelToken) {
        const { key, value } = cancelToken;
        axiosCancelToken = refreshCancelToken(value, key);
      }
      const res = await CommunityAPI.updateInteractionRule(selectedBrandId.value, ruleId, payload, {
        cancelToken: axiosCancelToken,
      });
      const updatedRule = res.data;
      const existingRuleIndex = interactionRules.value.findIndex((rule) => rule.id === ruleId);
      if (ruleModified) {
        interactionRules.value.splice(existingRuleIndex, 1);
        interactionRules.value.unshift(updatedRule);
      } else {
        interactionRules.value.splice(existingRuleIndex, 1, updatedRule);
      }
      notificationStore.setToast(notification);
    } catch (error) {
      const message =
        error.response?.status === 409 && error.response.data?.error
          ? error.response.data.error
          : COMMUNITY_INTERACTION_RULE_TOASTS.FAILURE;
      notificationStore.setToast({
        message,
        type: 'error',
      });
      return false;
    }
    return true;
  }

  async function deleteInteractionRule(rule) {
    await CommunityAPI.deleteInteractionRule(selectedBrandId.value, rule.id);
    interactionRules.value = interactionRules.value.filter((r) => r.id !== rule.id);
    messageRuleInteractionsEvent({
      interaction: MESSAGE_RULE_MIXPANEL_CONSTANTS.MESSAGE_RULE_INTERACTIONS.DELETE,
      ruleId: rule.id,
      ruleName: rule.name,
      ruleBrandIds: [selectedBrandId.value],
      ruleBrandNames: [selectedBrandName.value],
    });
  }

  function transformFiltersForPreview(filters) {
    if (!filters) return filters;
    const cloned = cloneDeep(filters);

    function transformItem(item) {
      if (item.key === AI_CATEGORY_FILTER_OPTION.key && item.value?.prompt) {
        return {
          ...item,
          key: AI_CATEGORY_FILTER_OPTION.previewKey,
          value: item.value.prompt,
        };
      }
      if (item.items) {
        return { ...item, items: item.items.map(transformItem) };
      }
      return item;
    }

    if (cloned.items) {
      cloned.items = cloned.items.map(transformItem);
    }
    return cloned;
  }

  async function getInteractionRulesPreview(filters) {
    const transformedFilters = transformFiltersForPreview(filters);
    const res = await CommunityAPI.getInteractionRulesPreview(
      selectedBrandId.value,
      transformedFilters,
    );
    return { status: res.status, data: res.data?.data ?? [] };
  }

  function setEditedRuleId(id, ruleBrandId = null) {
    editedRuleId.value = id;
    if (ruleBrandId) selectedBrandIdForInteractionRules.value = ruleBrandId;
  }

  function togglePopup(name, value) {
    showPopup.value[name] = !!value;
  }

  function openMessageRulePopup() {
    if (canAccessAutomationRuleGDI.value) {
      launchDrawer({ name: 'inboxAutomationRules' });
    } else {
      togglePopup('messageRules', true);
    }
    messageRulesSelectedEvent({
      ruleBrandIds: [selectedBrandId.value],
      ruleBrandNames: [selectedBrandName.value],
    });
  }

  async function openRuleBuilderToEdit(ruleId, fromMessage = false) {
    await setEditedRuleId(ruleId, editedRule.value?.brandId);
    if (canAccessAutomationRuleGDI.value) {
      launchDrawer({ name: 'inboxAutomationRuleWizard' });
    } else {
      togglePopup('messageRuleBuilder', true);
    }

    if (editedRule.value) {
      const option = fromMessage ? 'EDIT_FROM_MESSAGE' : 'EDIT';
      createOrEditMessageRuleEvent({
        openedFrom: MESSAGE_RULE_MIXPANEL_CONSTANTS.RULE_BUILDER_OPENED_FROM[option],
        ruleId,
        ruleName: editedRule.value.name,
        ruleBrandIds: [selectedBrandId.value],
        ruleBrandNames: [selectedBrandName.value],
      });
      messageRuleInteractionsEvent({
        interaction: MESSAGE_RULE_MIXPANEL_CONSTANTS.MESSAGE_RULE_INTERACTIONS[option],
        ruleId,
        ruleName: editedRule.value.name,
        ruleBrandIds: [selectedBrandId.value],
        ruleBrandNames: [selectedBrandName.value],
      });
    }
  }

  function formatTags(actionTagList = [], savedTagList = []) {
    const tagsMap = actionTagList.reduce((result, tag) => Object.assign(result, tag), {});
    const formatted = Object.keys(tagsMap).map((tagId) => {
      const savedTag = savedTagList.find((t) => t.id === Number(tagId));
      const item = { icon: { name: 'dot' } };
      if (savedTag) {
        item.icon.color = savedTag.color;
        item.text = savedTag.name;
        item.id = savedTag.id;
      } else {
        item.text = tagsMap[tagId];
        item.id = Number(tagId);
        item.error = true;
      }
      return item;
    });

    return formatted;
  }

  function getAuthorName(authorId) {
    if (authorId) {
      const authStore = useAuthStore();
      let brandAccessibleUsers;
      if (selectedBrandAccessibleUsers.value.length > 0) {
        brandAccessibleUsers = selectedBrandAccessibleUsers.value;
      } else {
        brandAccessibleUsers = authStore.brandAccessibleUsers;
      }
      const author = brandAccessibleUsers.find((u) => u.id === authorId);
      if (author) {
        return [author.firstName, author.lastName].join(' ');
      }
      return 'Unavailable User';
    }
    return 'System';
  }

  async function getSelectedBrandAccessibleUsers(brandId) {
    try {
      const response = await AuthAPI.getBrandUsers({
        brandId,
        sort: 'first_name,last_name',
      });
      const payload = response?.data;
      selectedBrandAccessibleUsers.value = payload?.data;
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        logger.error(
          `auth getBrandAccessibleUsers request returned authentication error: ${error.response?.data?.description}`,
          {},
          error,
        );
        return null;
      }
      throw error;
    }
  }

  async function updateInternalMessageRuleInteraction(interactionId, payload) {
    const communityInteractionStore = useCommunityInteractionStore();
    await communityInteractionStore.updateCommunityInteraction(
      interactionId,
      payload,
      false,
      null,
      null,
      true,
    );
    return true;
  }

  async function deleteInteractionRulesHistory(brandId, ruleExecutionHistoryId) {
    try {
      await CommunityAPI.deleteInteractionRulesHistory(brandId, ruleExecutionHistoryId);
      return true;
    } catch {
      throw new Error('Failed to delete interaction rule history');
    }
  }

  async function undoAction(message, deleteRuleExecutedMessage, undoHideFunction = null) {
    const communityInteractionStore = useCommunityInteractionStore();
    const interactionId = communityInteractionStore.selectedCommunityInteractionId;

    const performUndo = async () => {
      const payload = {};
      const revertPayload = {};
      if (message.action[RULE_ACTION_KEYS.ARCHIVE]) {
        payload.isArchived = false;
        revertPayload.isArchived = true;
      }
      if (message.action[RULE_ACTION_KEYS.TAGS]) {
        const tagIds = Object.keys(message.action[RULE_ACTION_KEYS.TAGS][0]).map(Number);
        payload.tagIds = { remove: tagIds };
        revertPayload.tagIds = { add: tagIds };
      }

      if (message.action[RULE_ACTION_KEYS.HIDE] && undoHideFunction) {
        try {
          await undoHideFunction({
            params: { commentId: interactionId, platform: message.platform },
            data: { isHidden: false },
          });
          // Endpoint we are using here does not accept hide updates, so that will be done separately
          payload.isArchived = false;
          revertPayload.isArchived = true;
        } catch {
          notificationStore.setToast({
            message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.ERROR,
            type: 'error',
          });
          return;
        }
      }

      try {
        const undoResult = await updateInternalMessageRuleInteraction(interactionId, payload);
        if (undoResult) {
          try {
            const deleteHistory = await deleteInteractionRulesHistory(
              identityStore.currentBrand.id,
              message.id,
            );
            if (deleteHistory) {
              deleteRuleExecutedMessage({ ...message, interactionId });
              notificationStore.setToast({
                message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.SUCCESS,
                type: 'success',
              });
            }
          } catch (error) {
            await updateInternalMessageRuleInteraction(interactionId, revertPayload);
            throw error;
          }
        }
        undoMessageRuleEvent({
          ruleId: message.ruleId,
          ruleName: message.triggeredRuleName,
          ruleBrandIds: [selectedBrandId.value],
          ruleBrandNames: [selectedBrandName.value],
        });
      } catch {
        notificationStore.setToast({
          message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.ERROR,
          type: 'error',
        });
      }
    };

    await undoRuleActionDialog().then(async (accepted) => {
      if (accepted) {
        await performUndo();
      }
    });
  }

  function checkRuleDeleted(ruleId) {
    const ruleFound = interactionRules.value.some((rule) => rule.id === ruleId);
    return !ruleFound;
  }

  function validateRuleNameExists(ruleName, ignoreId = null) {
    if (!ruleName) {
      return true;
    }
    let savedRules = interactionRules.value.filter(
      (r) => r.brandId === identityStore.currentBrand?.id,
    );
    if (savedRules) {
      const excludedIds = [editedRuleId.value];
      if (ignoreId) {
        excludedIds.push(ignoreId);
      }
      savedRules = savedRules.filter((r) => !excludedIds.includes(r.id));
    }
    return savedRules.every(({ name }) => name !== ruleName);
  }

  function resetRuleBuilder() {
    clearRuleBuilder();
    setEditedRuleId(undefined);
    switchedToNestedDrawer.value = false;
  }

  return {
    interactionRules,
    showPopup,
    pending,
    switchedToNestedDrawer,
    selectedBrandAiCategories,
    pendingAiCategory,
    hasInteractionRules,
    interactionRulesByBrand,
    getSelectedBrandInteractionRules,
    editedRuleId,
    editedRule,
    getInteractionRulesForBrands,
    getInteractionRulesPreview,
    getAiCategoriesForSelectedBrand,
    updateInteractionRule,
    updateInteractionRuleLegacy,
    createInteractionRule,
    createInteractionRuleLegacy,
    createAiCategory,
    togglePopup,
    openMessageRulePopup,
    setEditedRuleId,
    openRuleBuilderToEdit,
    formatTags,
    getAuthorName,
    actions,
    conditionGroups,
    isFiltersComplete,
    isActionsComplete,
    isRuleComplete,
    hasRepliedFilter,
    filterMatchedMessagesCount,
    ruleBuilderErrors,
    ruleDetail,
    ruleChannelSet,
    isSingleChannelRule,
    hasMultiChannelSelection,
    isChannelFilterOrCondition,
    updateGroup,
    removeFilterGroup,
    deleteInteractionRule,
    deleteInteractionRulesHistory,
    undoAction,
    checkRuleDeleted,
    updateGroupOperator,
    addGroupAfter,
    buildJSONRule,
    buildJSONRuleActions,
    buildJSONRuleFilters,
    clearRuleBuilder,
    updateFilterMatchCount,
    checkGroupHasCompleteItem,
    setRuleActionsFromJSON,
    setRuleFiltersFromJSON,
    isRuleDetailChanged,
    getFilterUsed,
    getFilterGroups,
    selectedBrandIdForInteractionRules,
    getSelectedBrandAccessibleUsers,
    selectedBrandAccessibleUsers,
    selectedBrandId,
    selectedBrand,
    selectedBrandName,
    validateRuleNameExists,
    extractRuleFilters,
    updateRuleFilterByKey,
    deleteAiCategory,
    clearFilterMessageCountAndError,
    resetRuleBuilder,
  };
});
